import React, { useState } from 'react';
import SignatureCanvas from './SignatureCanvas';

interface PersonalInfo {
  first_name: string;
  last_name: string;
  address: string;
  apt_no: string;
  city: string;
  postal_code: string;
  tel: string;
  cell: string;
}

interface ReferenceInfo {
  company: string;
  current_employment: boolean;
  position: string;
  hire_date: string;
  termination_date: string;
  contact_name: string;
  relationship: string;
  tel: string;
  additional_info: string;
}

interface ReferenceConsentFormProps {
  onSubmit: (data: {
    personal: PersonalInfo;
    references: ReferenceInfo[];
    signature_image: string;
  }) => Promise<void>;
  isSubmitted: boolean;
  signedAt?: string;
}

const emptyRef: ReferenceInfo = {
  company: '', current_employment: false, position: '',
  hire_date: '', termination_date: '', contact_name: '',
  relationship: '', tel: '', additional_info: '',
};

export default function ReferenceConsentForm({ onSubmit, isSubmitted, signedAt }: ReferenceConsentFormProps) {
  const [personal, setPersonal] = useState<PersonalInfo>({
    first_name: '', last_name: '', address: '', apt_no: '',
    city: '', postal_code: '', tel: '', cell: '',
  });
  const [references, setReferences] = useState<ReferenceInfo[]>([
    { ...emptyRef }, { ...emptyRef }, { ...emptyRef },
  ]);
  const [signatureImage, setSignatureImage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const updateRef = (index: number, field: string, value: any) => {
    setReferences(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!personal.first_name || !personal.last_name) {
      alert('이름을 입력해주세요.');
      return;
    }
    if (!signatureImage) {
      alert('서명을 해주세요.');
      return;
    }
    const hasRef = references.some(r => r.company && r.contact_name);
    if (!hasRef) {
      alert('추천인을 최소 1명 이상 입력해주세요.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ personal, references, signature_image: signatureImage });
    } finally {
      setSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
        <h3 style={{ fontSize: '18px', color: '#0f172a', marginBottom: '8px' }}>
          평판조회 동의서가 제출되었습니다
        </h3>
        <p style={{ fontSize: '14px', color: '#64748b' }}>
          제출일시: {signedAt ? new Date(signedAt).toLocaleString('ko-KR') : '-'}
        </p>
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', fontSize: '14px',
    border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '13px', color: '#475569', fontWeight: 500, marginBottom: '4px', display: 'block',
  };
  const gridStyle: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px',
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      {/* 동의 문구 */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
          Reference Check Consent and Release
        </h3>
        <p style={{ fontSize: '12px', color: '#334155', lineHeight: 1.7 }}>
          I hereby authorize GRK partners co.ltd to verify all the information I have provided and to obtain and
          disclose all personal information or all information regarding my person. This verification may be
          performed by phone or in writing, notably but not exclusively, before personal information agents, former
          employers, associations, current employers, public agencies, parapublic agencies, or any other source
          provided as reference.
        </p>
        <p style={{ fontSize: '12px', color: '#334155', lineHeight: 1.7, marginTop: '8px' }}>
          I authorize the individuals and organizations named above to furnish all information requested by GRK
          partners co.ltd and/or its representatives, on its behalf, and this, free of all and any liability.
        </p>
      </div>

      {/* 본인 정보 */}
      <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>본인 정보</h4>
      <div style={gridStyle}>
        <div>
          <label style={labelStyle}>First name *</label>
          <input style={inputStyle} value={personal.first_name} onChange={e => setPersonal(p => ({ ...p, first_name: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>Last name *</label>
          <input style={inputStyle} value={personal.last_name} onChange={e => setPersonal(p => ({ ...p, last_name: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>Address</label>
          <input style={inputStyle} value={personal.address} onChange={e => setPersonal(p => ({ ...p, address: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>Apartment no.</label>
          <input style={inputStyle} value={personal.apt_no} onChange={e => setPersonal(p => ({ ...p, apt_no: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>City</label>
          <input style={inputStyle} value={personal.city} onChange={e => setPersonal(p => ({ ...p, city: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>Postal code</label>
          <input style={inputStyle} value={personal.postal_code} onChange={e => setPersonal(p => ({ ...p, postal_code: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>Telephone no.</label>
          <input style={inputStyle} value={personal.tel} onChange={e => setPersonal(p => ({ ...p, tel: e.target.value }))} />
        </div>
        <div>
          <label style={labelStyle}>Cellphone no.</label>
          <input style={inputStyle} value={personal.cell} onChange={e => setPersonal(p => ({ ...p, cell: e.target.value }))} />
        </div>
      </div>

      {/* Reference 1~3 */}
      {references.map((ref, i) => (
        <div key={i} style={{ marginTop: '24px', padding: '16px', background: '#fafafa', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>
            Reference {i + 1} {i === 0 ? '*' : '(선택)'}
          </h4>
          <div style={gridStyle}>
            <div>
              <label style={labelStyle}>Company name {i === 0 ? '*' : ''}</label>
              <input style={inputStyle} value={ref.company} onChange={e => updateRef(i, 'company', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Current employment?</label>
              <div style={{ display: 'flex', gap: '16px', padding: '8px 0' }}>
                <label style={{ fontSize: '14px', cursor: 'pointer' }}>
                  <input type="radio" name={`emp-${i}`} checked={ref.current_employment} onChange={() => updateRef(i, 'current_employment', true)} /> Yes
                </label>
                <label style={{ fontSize: '14px', cursor: 'pointer' }}>
                  <input type="radio" name={`emp-${i}`} checked={!ref.current_employment} onChange={() => updateRef(i, 'current_employment', false)} /> No
                </label>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Position held</label>
              <input style={inputStyle} value={ref.position} onChange={e => updateRef(i, 'position', e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <label style={labelStyle}>Hire date</label>
                <input type="date" style={inputStyle} value={ref.hire_date} onChange={e => updateRef(i, 'hire_date', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Termination date</label>
                <input type="date" style={inputStyle} value={ref.termination_date} onChange={e => updateRef(i, 'termination_date', e.target.value)} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Contact person name {i === 0 ? '*' : ''}</label>
              <input style={inputStyle} value={ref.contact_name} onChange={e => updateRef(i, 'contact_name', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Relationship</label>
              <input style={inputStyle} value={ref.relationship} onChange={e => updateRef(i, 'relationship', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Telephone no.</label>
              <input style={inputStyle} value={ref.tel} onChange={e => updateRef(i, 'tel', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Additional information</label>
              <input style={inputStyle} value={ref.additional_info} onChange={e => updateRef(i, 'additional_info', e.target.value)} />
            </div>
          </div>
        </div>
      ))}

      {/* 서명 */}
      <div style={{ marginTop: '32px' }}>
        <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>서명 *</h4>
        <SignatureCanvas onSign={setSignatureImage} width={400} height={150} />
        <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
          Date: {new Date().toLocaleDateString('ko-KR')}
        </p>
      </div>

      {/* 제출 */}
      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            padding: '14px 48px', fontSize: '15px', fontWeight: 600,
            color: '#fff', background: submitting ? '#94a3b8' : '#002060',
            border: 'none', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? '제출 중...' : '동의서 제출하기'}
        </button>
      </div>
    </div>
  );
}
